import { NodeViewSettingsPanel } from '@/components/common/node-view-settings-panel';
import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { toTitleCase } from '@/lib/utils';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

export function ZyExampleNodeView(props: ReactNodeViewProps) {
    const { node, updateAttributes, selected, decorations } = props;
    const caption = node.attrs.caption || '';
    const type = node.attrs.type || 'example';
    const size = node.attrs.size || '';
    const [enumType, setEnumType] = useState(node.attrs.enumType || '');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const isEnumTypeEmpty = !enumType;

    // We do not want to show advanced settings for now see: https://zybooks.atlassian.net/browse/SPRINT-12023
    const showAdvancedSettings = false;
    const isTableType = type === 'table';

    const handleCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateAttributes({ caption: e.target.value });
    };

    const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value === '' ? null : e.target.value;
        updateAttributes({ size: value });
    };

    const handleEnumTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setEnumType(newValue);
        updateAttributes({ enumType: newValue || null });
    };

    const settingsPanelFields = [
        {
            label: 'Caption',
            type: 'text' as const,
            placeholder: 'Caption...',
            value: caption,
            onChange: handleCaptionChange,
            searchAttr: 'caption',
        },
        ...(type === 'enum'
            ? [
                  {
                      label: 'Enum type',
                      type: 'text' as const,
                      value: enumType,
                      onChange: handleEnumTypeChange,
                      placeholder: 'e.g. Theorem',
                      error: isEnumTypeEmpty ? 'Enum type is required' : undefined,
                      searchAttr: 'enumType',
                  },
              ]
            : []),
    ];

    const settingsPanel = <NodeViewSettingsPanel fields={settingsPanelFields} />;

    const advancedSettingsFooter = (
        <div>
            <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            >
                <ChevronRight className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                Advanced Settings
            </button>
            {showAdvanced && (
                <div className="mt-2 flex items-center">
                    <span className="w-16 text-xs font-medium">Size</span>
                    <select
                        value={size}
                        onChange={handleSizeChange}
                        className="ml-2 cursor-pointer rounded border px-2 py-1 text-sm capitalize"
                    >
                        <option value="">Default</option>
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                    </select>
                </div>
            )}
        </div>
    );

    const advancedSettingsFooterClassName = 'border-t border-gray-200 px-6 py-2 bg-white dark:border-gray-700';

    const mainContent = <NodeViewContent className="pt-4 bg-white dark:bg-black" />;

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label={toTitleCase(type)}
            selected={selected}
            settingsPanel={settingsPanel}
            footer={showAdvancedSettings ? advancedSettingsFooter : undefined}
            footerClassName={showAdvancedSettings ? advancedSettingsFooterClassName : undefined}
            fullscreenContent={mainContent}
            fullscreenMode="inline"
            contentHeightClassName=""
            contentClassName={isTableType ? 'pb-1' : undefined}
        >
            {mainContent}
        </CommonNodeViewWrapper>
    );
}
